<template>
  <div class="cert-access">
    <div v-if="state === 'loading'" class="cert-access__status">
      <span class="cert-access__spinner" aria-hidden="true"></span>
      <p class="cert-access__line">Opening your certificate…</p>
    </div>

    <div v-else-if="state === 'saved'" class="cert-access__status">
      <p class="cert-access__line">Your certificate has been downloaded.</p>
      <p class="cert-access__muted">{{ fileName }}</p>
      <div class="cert-access__actions">
        <a :href="fileUrl" :download="fileName" class="btn base-button btn-primary">Download again</a>
        <button type="button" class="btn base-button btn-default" @click="leave">Close</button>
      </div>
    </div>

    <div v-else-if="state === 'error'" class="cert-access__status">
      <p class="cert-access__line">{{ error }}</p>
      <div class="cert-access__actions">
        <button type="button" class="btn base-button btn-primary" @click="run">Try again</button>
        <button type="button" class="btn base-button btn-default" @click="leave">Close</button>
      </div>
    </div>

    <iframe v-else class="cert-access__frame" :src="fileUrl" title="Certificate"></iframe>
  </div>
</template>

<script>
import axios from 'axios';

function isAllowedCertificateApiUrl(target) {
  const base = process.env.VUE_APP_API_URL || '';
  if (!base || !target) return false;
  try {
    const u = new URL(target);
    const b = new URL(base);
    if (u.origin !== b.origin) return false;
    const p = u.pathname;
    return (
      p.includes('downloadCourseCertificate') ||
      p.includes('downloadCourseCertificateByEntry') ||
      p.includes('downloadAllCourseCertificate') ||
      p.includes('downloadCourseFoldersCertificate')
    );
  } catch {
    return false;
  }
}

/** The name the server gave the file, if it gave one. */
function fileNameFrom(disposition, fallback) {
  if (!disposition) return fallback;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      // fall through to the plain form
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(disposition);
  return plain ? plain[1].trim() : fallback;
}

/**
 * The tab a certificate link opens in.
 *
 * A certificate URL opened in a browser carries no Authorization header, so
 * the API redirects here; this page fetches the file with the signed-in
 * user's token and shows it.
 *
 * It used to hand the file to window.open() and then send the tab to the
 * dashboard. That open runs after the fetch, by which time the click that
 * started it is gone, so browsers blocked it: the certificate never appeared
 * and the user was left looking at the dashboard, as though the app had
 * thrown them out. This tab was opened for the certificate, so the
 * certificate is shown here -- in the page when the server sends it to be
 * read, saved when the server sends it to be kept -- and nothing navigates
 * anywhere on its own.
 */
export default {
  name: 'CertificateAccess',
  data() {
    return {
      state: 'loading',
      fileUrl: '',
      fileName: 'certificate.pdf',
      error: '',
      target: '',
    };
  },
  mounted() {
    this.run();
  },
  beforeDestroy() {
    this.forgetFile();
  },
  methods: {
    async run() {
      this.state = 'loading';
      this.error = '';
      this.forgetFile();

      const target = this.resolveTarget();
      if (!target) {
        this.state = 'error';
        this.error = 'That certificate link is not one we can open.';
        return;
      }

      const token = localStorage.getItem('hot-token');
      if (!token) {
        // Sign in first; Login sends the user back here afterwards.
        sessionStorage.setItem('certificate_download_target', target);
        this.$router.replace({ name: 'Login' });
        return;
      }
      sessionStorage.removeItem('certificate_download_target');

      try {
        const resp = await axios.get(target, {
          responseType: 'blob',
          headers: {
            Authorization: 'Bearer ' + token,
            Accept: 'application/pdf, application/octet-stream, */*',
          },
        });

        const disposition = resp.headers['content-disposition'] || '';
        const blob = new Blob([resp.data], {
          type: resp.headers['content-type'] || 'application/pdf',
        });
        this.fileUrl = URL.createObjectURL(blob);
        this.fileName = fileNameFrom(disposition, this.fallbackName(target));

        if (/attachment/i.test(disposition)) {
          this.save();
          this.state = 'saved';
        } else {
          this.state = 'inline';
        }
      } catch (e) {
        this.state = 'error';
        this.error = 'We could not open your certificate. Please try again.';
      }
    },
    /** Where the certificate is: the link that brought us, or one kept for after sign-in. */
    resolveTarget() {
      let target = this.$route.query.target;
      if (typeof target === 'string') {
        try {
          target = decodeURIComponent(target);
        } catch {
          // keep as-is
        }
      }
      if (!target) target = sessionStorage.getItem('certificate_download_target') || '';
      if (!isAllowedCertificateApiUrl(target)) return '';
      this.target = target;
      return target;
    },
    fallbackName(target) {
      const name = String(target).split('?')[0].split('/').filter(Boolean).pop();
      return name && name.indexOf('.') !== -1 ? name : 'certificate.pdf';
    },
    /** Hand the file to the browser to keep. A download is never a pop-up. */
    save() {
      const link = document.createElement('a');
      link.href = this.fileUrl;
      link.download = this.fileName;
      link.rel = 'noopener';
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    forgetFile() {
      if (this.fileUrl) {
        URL.revokeObjectURL(this.fileUrl);
        this.fileUrl = '';
      }
    },
    /** This tab was opened for the certificate, so closing it is the way out. */
    leave() {
      window.close();
      // A tab the app did not open itself cannot be closed; show something.
      this.$router.replace('/dashboard');
    },
  },
};
</script>

<style scoped>
.cert-access {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f8fafc;
}
.cert-access__frame {
  width: 100%;
  height: 100vh;
  border: 0;
  background: #525659;
}
.cert-access__status {
  text-align: center;
  padding: 2rem 1.25rem;
  color: #0f172a;
}
.cert-access__line { margin: 0 0 0.35rem; font-size: 1rem; }
.cert-access__muted { margin: 0; font-size: 0.85rem; color: #64748b; word-break: break-all; }
.cert-access__actions {
  margin-top: 1.1rem;
  display: flex;
  gap: 0.5rem;
  justify-content: center;
  flex-wrap: wrap;
}
.cert-access__spinner {
  display: inline-block;
  width: 28px;
  height: 28px;
  margin-bottom: 0.75rem;
  border: 3px solid #e2e8f0;
  border-top-color: #3e7799;
  border-radius: 50%;
  animation: cert-access-spin 0.8s linear infinite;
}
@keyframes cert-access-spin { to { transform: rotate(360deg); } }
</style>
